<template>
  <div
    v-if="pricing?.tieredPrice && pricing?.tieredPrice?.length != 0"
    :class="['pricing', isLargeCard ? 'is-large-card' : '']"
  >
    <template v-if="pricing?.priceType?.text && showPriceTypeAbovePrice">
      <div
        class="medium"
        data-testid="price-type-above-price"
      >
        {{ pricing?.priceType?.text }}
      </div>
    </template>
    <!-- Value pricing -->
    <template v-if="!isLargeCard">
      <template v-if="displaySalePriceOnly">
        <div class="price-reg">
          {{ getLabelWithPrice }}
        </div>
      </template>
      <template v-else-if="valuePricing">
        <div :class="{ 'price-reg': !hasDiscountOnValue }">
          <span :class="{ 'price-strike': hasDiscountOnValue }">{{
            valuePricing?.price
          }}</span>
          &nbsp;
          <span>({{ valuePricing?.value }} Value)</span>
        </div>
        <span
          v-if="hasDiscountOnValue"
          :class="[
            valuePriceOnSale?.saleType != 'regular' && !hasFinalPrice
              ? 'discount'
              : 'price-reg',
          ]"
        >
          {{ valuePriceOnSale?.saleValue }}</span>
        <template v-if="valuePriceSaleRange">
          <span
            :class="[
              valuePriceSaleRange?.saleType != 'regular' && !hasFinalPrice
                ? 'discount'
                : 'price-reg',
            ]"
          >
            {{ valuePriceSaleRange?.saleValue }}</span>
        </template>
      </template>
      <template v-else>
        <div
          v-for="(price, tieredPriceIndex) in (pricing?.tieredPrice || [])"
          :key="tieredPriceIndex"
          :class="{ 'show-percent-off': (isBcom || showPercentOff) && percentOff && tieredPriceIndex === 1 }"
        >
          <span
            v-for="(value, valuesIndex) in (price?.values || [])"
            :key="value"
            :class="getClasses(value?.type, tieredPriceIndex)"
          >
            <span v-if="tieredPriceIndex > 0 && valuesIndex === 0">
              {{ getPriceLabel(price?.label) }}
            </span>
            {{ value?.formattedValue }}
            <span v-if="price?.values?.length === 2 && valuesIndex === 0">
              -
            </span>
          </span>
        </div>
        <span
          v-if="(showPercentOff || isBcom) && percentOff"
          :class="[hasFinalPrice ? 'price-reg' : 'discount']"
        >
          ({{ percentOff }}% OFF)
        </span>
        <div
          v-if="isBcom && discountedPrice?.saleType === 'edv'"
          class="final-price eyebrow"
        >
          THE FINAL OFFER
        </div>
        <div
          v-if="isBcom &&
            pricing?.priceType?.selectItemsOnSale &&
            (pricing?.priceType?.memberProductOnSale || pricing?.priceType?.upcOnSale)"
          class="eyebrow discount"
        >
          SELECT ITEMS ON SALE
        </div>
      </template>
      <!-- In response, if final price is part of pricing but not part of badges  -->
      <template v-if="pricing?.finalPrice && !isLargeCard && !displaySalePriceOnly">
        <div>
          <span :class="finalPrice?.saleType"> {{ finalPrice?.saleValue }}</span>
          <span
            v-if="finalPriceWithRange?.saleValue"
            :class="finalPriceWithRange?.saleType"
          >
            {{ finalPriceWithRange?.saleValue }}
          </span>
        </div>
      </template>
      <div
        v-if="!isBcom && pricing?.priceType?.memberProductOnSale"
        class="margin-top-xxs"
      >
        <div class="h6 select-on-sale">
          Select Items On Sale
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import isBcom from '../../../utils/isBcom';

export default {
  name: 'PricingBlock',
  props: {
    pricing: {
      type: Object,
      default: () => { },
    },
    isFinalPrice: {
      type: Boolean,
      default: false,
    },
    isLargeCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPercentOff: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPriceTypeAbovePrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    displaySalePriceOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isBcom,
    };
  },
  computed: {
    // Value pricing with discount
    hasDiscountOnValue() {
      return this.pricing?.tieredPrice?.length === 3;
    },
    hasDiscount() {
      return this.pricing?.tieredPrice?.length > 1;
    },
    // product with Value price on sale
    valuePriceOnSale() {
      const label = this.getPriceLabel(this.pricing?.tieredPrice[2]?.label);
      if (
        this.pricing?.tieredPrice?.length === 3
        && this.pricing?.tieredPrice[2]?.values[0]?.type !== 'regular'
      ) {
        return {
          saleValue: `${label}${this.pricing?.tieredPrice[2].values[0].formattedValue}`,
          saleType: this.pricing?.tieredPrice[2].values[0].type,
        };
      }
      return null;
    },
    // product with Value price on sale w/ range
    valuePriceSaleRange() {
      if (this.pricing?.tieredPrice[2]?.values[1]) {
        return {
          saleValue: ` - ${this.pricing?.tieredPrice[2].values[1].formattedValue}`,
          saleType: this.pricing?.tieredPrice[2].values[1].type,
        };
      }
      return null;
    },
    valuePricing() {
      if (this.pricing?.tieredPrice[0]?.label?.indexOf('value') > -1) {
        return {
          price: this.pricing?.tieredPrice[1].values[0].formattedValue,
          value: this.pricing?.tieredPrice[0].values[0].formattedValue,
        };
      }
      return null;
    },
    discountedPrice() {
      const label = this.getPriceLabel(this.pricing?.tieredPrice[1]?.label);
      if (this.pricing?.tieredPrice?.length === 2) {
        return {
          saleValue: `${label}${this.pricing?.tieredPrice[1].values[0].formattedValue}`,
          saleType: this.pricing?.tieredPrice[1]?.values[0].type,
        };
      }
      return null;
    },
    hasFinalPrice() {
      if (this.isFinalPrice || this.pricing?.finalPrice?.label) {
        return true;
      }
      return false;
    },
    finalPrice() {
      const label = this.getPriceLabel(this.pricing?.finalPrice?.label);
      if (this.pricing?.finalPrice?.values[0]?.type !== 'regular') {
        return {
          saleValue: `${label}${this.pricing?.finalPrice?.values[0].formattedValue}`,
          saleType: this.pricing?.finalPrice?.values[0].type,
        };
      }
      return null;
    },
    finalPriceWithRange() {
      if (this.pricing?.finalPrice.values[1]) {
        return {
          saleValue: ` - ${this.pricing?.finalPrice.values[1].formattedValue}`,
          saleType: this.pricing?.finalPrice.values[1].type,
        };
      }
      return null;
    },
    getLabelWithPrice() {
      const lastTieredPrice = this.pricing.finalPrice || this.pricing.tieredPrice[this.pricing.tieredPrice.length - 1];
      const values = lastTieredPrice.values.sort((a, b) => (a.value - b.value));
      const label = this.getPriceLabel(lastTieredPrice.label).trim();
      let price = values[0].formattedValue;
      if (values.length > 1) {
        price += ` - ${values[values.length - 1].formattedValue}`;
      }
      return `${label} ${price}`.trim();
    },
    // applicable only for bcom
    percentOff() {
      let percentOff = '';
      if (this.pricing?.tieredPrice[1]?.values[0]?.percentOff) {
        percentOff = `${this.pricing?.tieredPrice[1]?.values[0]?.percentOff[0]}`;
      }
      if (this.pricing?.tieredPrice[1]?.values[1]?.percentOff) {
        percentOff = `${percentOff}-${this.pricing?.tieredPrice[1]?.values[1]?.percentOff[0]}`;
        return percentOff;
      }
      return percentOff;
    },
  },
  methods: {
    // get label of the price.
    // ex: sale, now, and value
    getPriceLabel(label) {
      const priceIndex = label?.indexOf('[PRICE]');
      return label?.substring(0, priceIndex);
    },
    getClasses(priceType, tieredPriceIndex) {
      if (this.pricing?.tieredPrice?.length > 1 && tieredPriceIndex === 0) {
        return 'price-strike';
      } if (
        ['regular', 'NO_LABEL'].indexOf(priceType) !== -1
        || this.hasFinalPrice
        || this.pricing?.tieredPrice?.length - 1 > tieredPriceIndex
      ) {
        return 'price-reg';
      }
      return 'discount price-reg';
    },
  },
};
</script>
<style lang="scss" scoped>
.pricing {
  .discount {
    color: $sale-color;
    font-weight: $font-weight-heavy;
  }

  .show-percent-off {
    display: inline-block;
  }

  &.no-margin-top {
    margin-top: 0;
  }
  .select-on-sale {
    color: #333;
  }
}
</style>
<style lang="scss" brand="bcom" scoped>
.pricing {
  .price-reg {
    font-weight: $font-weight-heavy;
  }
  .final-price {
    color: $gray-1-color;
  }
}
</style>
